import { useEffect } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { KpiCard } from '@/components/px/KpiCard'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { PhaseRibbon } from '@/components/px/PhaseRibbon'
import { SectionLabel } from '@/components/px/SectionLabel'
import { projectNav } from '@/components/px/nav'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ANCHORS,
  BLUEPRINT_EPICS,
  BLUEPRINT_TABLE,
  DATA_MODEL,
  DATA_MODEL_CHAIN,
  type EpicStatus,
} from '@/demo/content/blueprint'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { DecisionsPanel } from '@/features/decisions/components/DecisionsPanel'
import {
  decisionsClosed,
  selectDecisionRows,
  selectDecisionsBlocked,
  selectDecisionsLoading,
  selectOpenDecisions,
} from '@/features/decisions/slices/decisionsSlice'
import { StatusPill } from '@/components/px/StatusPill'
import type { PhaseGateView } from '@/features/phase-gates/types'
import {
  gatesClosed,
  gatesErrorDismissed,
  gatesRefetchArmed,
  openGates,
  selectGates,
  selectGatesError,
  selectGatesLoading,
  selectGatesNonce,
  selectPhaseView,
} from '@/features/phase-gates/slices/phaseGatesSlice'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import { epochLabel, toRibbonPhase } from '@/features/projects/format'
import {
  clearProjectDetailError,
  closeProject,
  openProject,
  selectProjectBlocked,
  selectProjectDetail,
  selectProjectDetailError,
} from '@/features/projects/slices/projectDetailSlice'
import { asApiError } from '@/services/apiError'
import { useAppDispatch, useAppSelector } from '@/store'
import { cn } from '@/lib/utils'

import { projectOverviewRail } from './ProjectOverviewRail'

const EPIC_TONE: Record<EpicStatus, 'decided' | 'review'> = {
  Signed: 'decided',
  'Open decision': 'review',
}

/** The one auditable fixture seam: unsigned Blueprint structure has no backend model yet. */
function BlueprintStructureSection(): React.JSX.Element {
  return (
    <section
      data-testid="demo-band"
      id={ANCHORS.blueprint}
      aria-labelledby="blueprint-structure-heading"
      className="mt-7 rounded-lg border border-border bg-card"
    >
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <ConceptBadge />
          <SectionLabel>{BLUEPRINT_TABLE.label}</SectionLabel>
        </div>
        <h2
          id="blueprint-structure-heading"
          className="mt-0.5 font-serif text-lg font-semibold text-foreground"
        >
          {BLUEPRINT_TABLE.heading}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {BLUEPRINT_TABLE.agentLine}
        </p>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{BLUEPRINT_TABLE.columns.epic}</TableHead>
              <TableHead>{BLUEPRINT_TABLE.columns.approach}</TableHead>
              <TableHead className="text-right">
                {BLUEPRINT_TABLE.columns.modules}
              </TableHead>
              <TableHead>{BLUEPRINT_TABLE.columns.status}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {BLUEPRINT_EPICS.map((epic) => (
              <TableRow
                key={epic.key}
                className={cn(
                  epic.status === 'Open decision' && 'bg-review-bg font-medium'
                )}
              >
                <TableCell className="px-4 text-sm text-foreground">
                  <span className="v-figure mr-2 tabular-nums text-muted-foreground">
                    {epic.index}
                  </span>
                  <span className="font-medium">{epic.name}</span>
                </TableCell>
                <TableCell className="whitespace-normal">
                  <span className="flex flex-col leading-tight">
                    <span className="text-sm text-foreground">
                      {epic.approach}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {epic.approachNote}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="v-figure text-right text-sm tabular-nums text-foreground">
                  {epic.modules}
                </TableCell>
                <TableCell className="px-4">
                  <span className="flex flex-col items-start gap-1">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        EPIC_TONE[epic.status] === 'review'
                          ? 'bg-review-bg text-review-ink'
                          : 'bg-primary text-primary-foreground'
                      )}
                    >
                      {epic.status}
                    </span>
                    {epic.blockedBy !== undefined && (
                      <a
                        href={`#${ANCHORS.decisions}`}
                        className="text-xs font-medium text-accent-foreground hover:underline"
                      >
                        {epic.blockedBy} · open decision →
                      </a>
                    )}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <section
        id={ANCHORS.dataModel}
        aria-labelledby="blueprint-data-model-heading"
        className="border-t border-border px-4 py-4"
      >
        <SectionLabel>{DATA_MODEL.label}</SectionLabel>
        <h3
          id="blueprint-data-model-heading"
          className="mt-0.5 font-serif text-base font-semibold text-foreground"
        >
          {DATA_MODEL.heading}
        </h3>
        <p className="mt-1 max-w-3xl text-sm text-ink-soft">
          {DATA_MODEL.lead}
        </p>
        <ol className="mt-3 flex flex-wrap items-stretch gap-2">
          {DATA_MODEL_CHAIN.map((entity, index) => (
            <li key={entity.key} className="flex items-center gap-2">
              <span className="flex min-w-40 flex-col rounded-md border border-border bg-muted px-3 py-2">
                <span className="text-sm font-semibold text-foreground">
                  {entity.name}
                </span>
                <span className="v-figure font-serif text-lg font-semibold tabular-nums text-foreground">
                  {entity.count}
                </span>
                <span className="text-xs text-muted-foreground">
                  {entity.unit}
                </span>
              </span>
              {index < DATA_MODEL_CHAIN.length - 1 && (
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              )}
            </li>
          ))}
        </ol>
        <p className="mt-3 border-t border-border pt-2.5 text-xs text-muted-foreground">
          {DATA_MODEL.caption}{' '}
          <Link
            to={DATA_MODEL.captionHref}
            className="font-medium text-accent-foreground hover:underline"
          >
            {DATA_MODEL.captionLinkLabel} →
          </Link>
        </p>
      </section>
    </section>
  )
}

/**
 * The Design gate's state only, read-only (design shot 26): the Blueprint is
 * not the gates screen, so it carries no approve / submit / return actions.
 */
function DesignGateCard({ gate }: { gate: PhaseGateView }): React.JSX.Element {
  const { t } = useTranslation('projectWorkspace')
  return (
    <article
      className="rounded-lg border border-border bg-card px-4 py-4"
      data-testid="blueprint-design-gate"
      data-gate-ref={gate.ref}
      data-gate-open={gate.open ? 'true' : 'false'}
      aria-label={t('blueprint.gate.card')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <SectionLabel>{t('blueprint.gate.title')}</SectionLabel>
        <StatusPill tone={gate.open ? 'review' : 'decided'}>
          {gate.open ? t('blueprint.gate.open') : t('blueprint.gate.decided')}
        </StatusPill>
      </div>
      <h3 className="mt-1 font-serif text-lg font-semibold text-foreground">
        {gate.title}
      </h3>
      <ul className="mt-2 space-y-1 text-sm text-ink-soft">
        <li>
          {gate.criteriaAllMet
            ? t('blueprint.gate.criteriaMet')
            : t('blueprint.gate.criteriaPending')}
        </li>
        {gate.open && gate.ageDays !== undefined && (
          <li>{t('blueprint.gate.openDays', { count: gate.ageDays })}</li>
        )}
        {!gate.open && gate.decidedAt !== undefined && (
          <li>
            {t('blueprint.gate.decidedBy', {
              name: gate.decidedByName ?? gate.decidedBy ?? '—',
              when: epochLabel(gate.decidedAt),
            })}
          </li>
        )}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">
        {t('blueprint.gate.readOnly')}
      </p>
    </article>
  )
}

/** Design 3.4: live engagement context, live decisions/gates, one honest structure band. */
export default function BlueprintPage() {
  const { t } = useTranslation('projectWorkspace')
  const { projectId } = useParams()
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const project = useAppSelector(selectProjectDetail)
  const projectBlocked = useAppSelector(selectProjectBlocked)
  const projectError = useAppSelector(selectProjectDetailError)
  const decisionBlocked = useAppSelector(selectDecisionsBlocked)
  const decisions = useAppSelector(selectDecisionRows)
  const openCount = useAppSelector(selectOpenDecisions).length
  const decisionsLoading = useAppSelector(selectDecisionsLoading)
  const gates = useAppSelector(selectGates)
  const gatesError = useAppSelector(selectGatesError)
  const gatesLoading = useAppSelector(selectGatesLoading)
  const gatesNonce = useAppSelector(selectGatesNonce)
  const phase = useAppSelector(selectPhaseView)

  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(openProject({ orgId, projectId }))
    void dispatch(openGates({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  useEffect(() => {
    return () => {
      dispatch(closeProject())
      dispatch(gatesClosed())
      dispatch(decisionsClosed())
    }
  }, [dispatch, orgId, projectId])

  useEffect(() => {
    if (orgId === null || projectId === undefined || gatesNonce === 0) return
    void dispatch(openGates({ orgId, projectId }))
  }, [dispatch, orgId, projectId, gatesNonce])

  const retryProject = (): void => {
    dispatch(clearProjectDetailError())
    if (orgId !== null && projectId !== undefined)
      void dispatch(openProject({ orgId, projectId }))
  }

  const retryGates = (): void => {
    dispatch(gatesErrorDismissed())
    dispatch(gatesRefetchArmed())
    if (orgId !== null && projectId !== undefined)
      void dispatch(openGates({ orgId, projectId }))
  }

  const blocked = projectBlocked ?? decisionBlocked
  const rail = projectOverviewRail(projectId, project)
  const ribbonPhase = toRibbonPhase(
    phase?.currentPhase ?? project?.currentPhase ?? null
  )
  const overdueCount = decisions.filter((decision) => decision.overdue).length
  const designGate = gates.find((gate) => gate.ref === 'flow.04')
  // Primary-read failures are distinct from row-scoped decision refusals.
  const gateListError =
    gatesError !== null && gatesError.gateRef === undefined ? gatesError : null

  let content: React.ReactNode
  if (blocked !== null) {
    content = <ProjectBlockedState blocked={blocked} />
  } else if (project === null) {
    content =
      projectError !== null ? (
        <ErrorPanel
          className="mx-auto max-w-xl"
          error={asApiError(projectError)}
          onRetryClicked={retryProject}
        />
      ) : (
        <LoadingPanel label={t('blueprint.loading')} />
      )
  } else if (orgId === null || projectId === undefined) {
    content = <LoadingPanel label={t('blueprint.loading')} />
  } else {
    content = (
      <div className="w-full" data-testid="blueprint-live-workspace">
        <header className="max-w-4xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">
            {t('blueprint.title')}
          </h1>
          <p className="mt-1.5 text-sm text-ink-soft">
            {t('blueprint.standfirst')}
          </p>
        </header>

        {ribbonPhase !== null && (
          <PhaseRibbon current={ribbonPhase} className="mt-4" />
        )}

        <section
          className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3"
          aria-label={t('blueprint.summaryLabel')}
        >
          <KpiCard
            label={t('blueprint.summary.open')}
            figure={decisionsLoading ? '—' : openCount}
            qualifier={t('blueprint.summary.openQualifier')}
          />
          <KpiCard
            label={t('blueprint.summary.overdue')}
            figure={decisionsLoading ? '—' : overdueCount}
            qualifier={t('blueprint.summary.overdueQualifier')}
          />
          <KpiCard
            label={t('blueprint.summary.gate')}
            figure={
              designGate === undefined
                ? '—'
                : designGate.open
                  ? t('blueprint.gate.open')
                  : t('blueprint.gate.decided')
            }
            qualifier={t('blueprint.summary.gateQualifier')}
          />
        </section>

        <BlueprintStructureSection />
        <DecisionsPanel orgId={orgId} projectId={projectId} />

        <section aria-labelledby="blueprint-gates-heading" className="mt-7">
          <h2 id="blueprint-gates-heading" className="sr-only">
            {t('blueprint.gate.title')}
          </h2>
          {gatesLoading ? (
            <LoadingPanel label={t('blueprint.gate.loading')} />
          ) : gateListError !== null ? (
            <ErrorPanel
              error={asApiError({
                status: gateListError.status,
                key: gateListError.code,
                message: gateListError.message,
              })}
              onRetryClicked={retryGates}
            />
          ) : gates.length === 0 ? (
            <EmptyPanel
              title={t('blueprint.gate.empty.title')}
              body={t('blueprint.gate.empty.body')}
            />
          ) : designGate === undefined ? (
            <EmptyPanel
              title={t('blueprint.gate.empty.title')}
              body={t('blueprint.gate.notFound')}
            />
          ) : (
            <DesignGateCard gate={designGate} />
          )}
        </section>
      </div>
    )
  }

  return (
    <AppShell
      moduleLabel={t('blueprint.moduleLabel')}
      breadcrumb={project?.name ?? projectId ?? t('blueprint.breadcrumb')}
      contextPill={t('contextPill')}
      nav={projectNav(rail)}
    >
      {content}
    </AppShell>
  )
}
